import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { BarChart, FunnelChart, LineChart, PieChart } from 'echarts/charts';
import { DataZoomComponent, GridComponent, LegendComponent, TooltipComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import ReactEchart from '../../../../components/common/ReactEchart';
import { useAppContext } from '../../../../providers/AppProvider';
import StatisticsCardLoading from '../../common/statistic-chart-card/StatisticsChartCardLoading';
import StatisticsChartEmpty from '../../common/StatisticsChartEmpty';
import { STATISTICS_EMPTY_CHART } from '../../common/lib/statisticsEmptyStateCopy';
import StatisticsCount from '../../common/StatisticsCount';
import StatisticsDonutLabelModeSwitch from '../../common/StatisticsDonutLabelModeSwitch';
import useStatisticsChartTheme from '../../common/hooks/useStatisticsChartTheme';
import { getStatisticsAccentColor, resolveTrendChartPalette } from '../../common/lib/statisticsChartPalette';
import StatisticsChartFrame from '../../common/StatisticsChartFrame';
import usePreviewChartPaintGate from '../shared/hooks/usePreviewChartPaintGate';
import {
	DONUT_PIE_LABEL_MODE,
	getPrimaryPhaseChartTheme,
	mapDonutPieDataWithLabel,
	resolveLayeredMetricPieRadii,
	PIE_BINARY_SLICE_MIN_ANGLE,
	PIE_BINARY_SLICE_PAD_ANGLE,
	PIE_MULTI_RING_BINARY_SLICE_MIN_ANGLE,
} from '../pipeline/lib/orderPrimaryPhaseChartTheme';import { buildCompareAxisPointer, buildCompareValueYAxis } from '../trend/lib/trendChartOptionShared';
import { buildMetricSeriesItem } from '../trend/lib/trendMultiMetricOptionBuilders';
import {
	resolveVerticalCompareBarGapSeriesProps,
	resolveHorizontalCompareBarGridBottom,
	buildMixedCompareCategoryXAxes,
	buildPeriodCompareCategoryAxisLayout,
	buildCategoryAxisDataZoom,
	CATEGORY_AXIS_VISIBLE_COUNT,
	MIXED_COMPARE_BAR_X_AXIS_INDEX,
	MIXED_COMPARE_LINE_X_AXIS_INDEX,
	MIXED_COMPARE_LINE_Z,
	resolveMixedCompareBarSeriesExtra,
} from '../trend/lib/trendChartAxisLayout';
import { buildMixedOrAxisCompareTooltip } from '../trend/lib/trendMixedChartHelpers';
import {
	isCompositionChartMultiMetricMixedType,
	normalizeCompositionChartMetricSeriesTypes,
	resolveCompositionChartMetricBindings,
	resolveCompositionChartYAxisLabels,
} from './lib/compositionChartConfig';
import {
	buildPrimaryPhaseMetricTooltipLine,
	formatPrimaryPhaseMetricValue,
	getPrimaryPhaseMetricLabel,
	getPrimaryPhaseRowMetricValue,
	isPrimaryPhaseCurrencyMetric,
	ORDER_PRIMARY_PHASE_METRICS,
	sortActiveMetrics,
} from '../pipeline/lib/orderPrimaryPhaseMetric';
import { getOrderPrimaryPhaseColorType } from '../pipeline/lib/orderPrimaryPhaseConfig';
import CompositionChartMap from './CompositionChartMap';
import StatisticsChartSummaryStats from '../../common/StatisticsChartSummaryStats';
import { applyChartSummaryLegend, enrichMetricLegendWithSummary } from '../shared/lib/chartSummaryLegend';
import { resolveMetricSeriesIsBar } from '../shared/lib/metricSeriesTypes';
import {
	COMPOSITION_CHART_DIMENSION,
	COMPOSITION_CHART_TYPE,
	isCompositionChartBarType,
	isCompositionChartFunnelType,
	isCompositionChartLineType,
	isCompositionChartMapType,
	isCompositionChartPieLikeType,
	isCompositionChartFixedSliceDimension,
	normalizeCompositionChartConfig,
} from './lib/compositionChartConfig';
import { buildCompositionChartRowsFromStats } from './lib/compositionChartBuckets';
import { isHorizontalBarOrientation, resolveBarBorderRadius, resolveBarLabelPosition } from '../shared/lib/barOrientation';
import { useStatisticsContext } from '../../../../providers/StatisticsProvider';

echarts.use([TooltipComponent, LegendComponent, GridComponent, DataZoomComponent, PieChart, BarChart, LineChart, FunnelChart, CanvasRenderer]);

const BRAND_COLOR_DIMENSIONS = new Set([COMPOSITION_CHART_DIMENSION.SALE_USER, COMPOSITION_CHART_DIMENSION.ASSISTANT_USER, COMPOSITION_CHART_DIMENSION.CUSTOMER, COMPOSITION_CHART_DIMENSION.PROVINCE]);

const resolveCompositionChartSliceColor = (dimension, row, index, getThemeColor, isLight) => {
	if (dimension === COMPOSITION_CHART_DIMENSION.PRIMARY_PHASE) {
		return getThemeColor(getOrderPrimaryPhaseColorType(row.key));
	}
	if (dimension === COMPOSITION_CHART_DIMENSION.IS_COMPLETED) {
		return getThemeColor(row.key === 'completed' ? 'success' : 'secondary');
	}
	if (BRAND_COLOR_DIMENSIONS.has(dimension)) {
		return getStatisticsAccentColor(isLight, getThemeColor);
	}
	return getThemeColor(DONUT_FALLBACK_COLORS[index % DONUT_FALLBACK_COLORS.length]);
};

const DONUT_FALLBACK_COLORS = ['success', 'info', 'primary', 'warning', 'danger', 'secondary', 'dark'];

const METRIC_SERIES_COLORS = ['#6366f1', '#38bdf8', '#34d399', '#f59e0b', '#fb7185', '#a78bfa'];

const resolveMetricSeriesColor = (index, accentColor, isLight) => {
	const { yearLines } = resolveTrendChartPalette(isLight);
	return index === 0 ? accentColor : (yearLines[index % yearLines.length] ?? METRIC_SERIES_COLORS[index % METRIC_SERIES_COLORS.length]);
};

const formatMetricValue = (value, metric) => formatPrimaryPhaseMetricValue(value, metric);

const buildFixedSliceLegendEntries = (seriesRows, dimension, getThemeColor, isLight) =>
	seriesRows.map((row, index) => ({
		name: row.label,
		icon: 'circle',
		itemStyle: {
			color: resolveCompositionChartSliceColor(dimension, row, index, getThemeColor, isLight),
		},
	}));

const buildFixedSliceLegendOption = (seriesRows, dimension, getThemeColor, isLight, chartTheme, position = 'bottom') => ({
	show: seriesRows.length > 1,
	[position]: 0,
	left: 'center',
	itemWidth: 8,
	itemHeight: 8,
	textStyle: { color: chartTheme.labelColor, fontSize: 10 },
	data: buildFixedSliceLegendEntries(seriesRows, dimension, getThemeColor, isLight),
});

const buildFixedSlicePieSeriesLayout = ({ multiMetricPie, chartTheme }) => ({
	minAngle: multiMetricPie ? PIE_MULTI_RING_BINARY_SLICE_MIN_ANGLE : PIE_BINARY_SLICE_MIN_ANGLE,
	padAngle: PIE_BINARY_SLICE_PAD_ANGLE,
	itemStyle: {
		borderColor: chartTheme.pieBorderColor,
		borderWidth: 1,
	},
	emphasis: {
		scale: true,
		scaleSize: 6,
	},
});

const formatCompositionChartSliceTruePercent = (seriesRows, row, metric) => {
	const rawValue = getPrimaryPhaseRowMetricValue(row, metric);
	const total = seriesRows.reduce((sum, item) => sum + getPrimaryPhaseRowMetricValue(item, metric), 0);
	if (!total || rawValue <= 0) return '0';
	return ((rawValue / total) * 100).toFixed(1);
};

const CompositionChart = ({
	orders = [],
	chartConfig,
	compareSeriesMatrix = null,
	selectedPhase = null,
	isLoading = false,
	animationKey = 'default',
	height = '9rem',
	previewHeight = null,
	fillPreview = false,
	enableOrderDrill = false,
	onDrillSelect,
	primaryPhaseStats: primaryPhaseStatsProp = null,
	darkShell = false,
}) => {
	const { getThemeColor } = useAppContext();
	const statsContext = useStatisticsContext();
	const primaryPhaseStats = primaryPhaseStatsProp ?? statsContext?.primaryPhaseStats ?? null;
	const { isLight: themeIsLight } = useStatisticsChartTheme();
	const isLight = darkShell ? false : themeIsLight;
	const chartTheme = useMemo(() => getPrimaryPhaseChartTheme(isLight), [isLight]);
	const config = useMemo(() => normalizeCompositionChartConfig(chartConfig), [chartConfig]);
	const isPreview = Boolean(previewHeight) || fillPreview;
	const slotHeight = previewHeight || height;
	const frameStyle = isPreview ? { height: '100%', width: '100%', minHeight: 0 } : { minHeight: height };
	const echartStyle = isPreview ? { height: '100%', width: '100%', minHeight: 0 } : { minHeight: height };
	const previewClass = isPreview ? 'statistics-composition-chart--preview' : '';
	const { showPaintSkeleton, onChartReady: onPreviewChartReady } = usePreviewChartPaintGate(
		isPreview && !isLoading,
		`${animationKey}:${config.chartType}:${config.dimension}`,
	);
	const wrapPreviewPaint = (node) => {
		if (!isPreview) return node;
		return (
			<div
				className={['statistics-composition-chart-paint-shell', previewClass].filter(Boolean).join(' ')}
				style={{ ...frameStyle, position: 'relative' }}
			>
				{showPaintSkeleton ? (
					<div className='statistics-chart-preview-paint-gate' aria-hidden='true'>
						<StatisticsCardLoading className='statistics-lite-chart__loading statistics-lite-chart__loading--fill' fill />
					</div>
				) : null}
				<div
					style={{ height: '100%', visibility: showPaintSkeleton ? 'hidden' : 'visible' }}
					aria-hidden={showPaintSkeleton || undefined}
				>
					{node}
				</div>
			</div>
		);
	};
	const isDonut = config.chartType === COMPOSITION_CHART_TYPE.DONUT;
	const isPieLike = isCompositionChartPieLikeType(config.chartType);
	const isFunnel = isCompositionChartFunnelType(config.chartType);
	const isLine = isCompositionChartLineType(config.chartType);
	const isBar = isCompositionChartBarType(config.chartType);
	const isMap = isCompositionChartMapType(config.chartType);
	const horizontalBar = isBar && isHorizontalBarOrientation(config.barOrientation);
	const compareSeriesPie = isPieLike && Array.isArray(compareSeriesMatrix?.series) && compareSeriesMatrix.series.length > 1;
	const compareSeriesList = useMemo(
		() => (compareSeriesPie ? compareSeriesMatrix.series : []),
		[compareSeriesMatrix, compareSeriesPie],
	);
	const activeMetrics = useMemo(
		() => (compareSeriesPie ? [compareSeriesMatrix.metric || config.metric] : config.metrics),
		[compareSeriesMatrix, compareSeriesPie, config.metric, config.metrics],
	);
	const primaryMetric = compareSeriesPie ? compareSeriesMatrix.metric || config.metric : config.metric;
	const currencyMetric = isPrimaryPhaseCurrencyMetric(primaryMetric);
	const multiMetricPie = isPieLike && activeMetrics.length > 1 && !compareSeriesPie;
	const multiRingPie = multiMetricPie || compareSeriesPie;
	const multiMetricCompare = activeMetrics.length > 1 && (isBar || isLine);
	const isMultiMetricMixed = multiMetricCompare && isCompositionChartMultiMetricMixedType(config);
	const resolvedMetricSeriesTypes = useMemo(
		() => normalizeCompositionChartMetricSeriesTypes(activeMetrics, config.metricSeriesTypes, config.chartType),
		[activeMetrics, config.chartType, config.metricSeriesTypes],
	);
	const fixedSliceDimension = isCompositionChartFixedSliceDimension(config.dimension);
	const showPieFunnelFooter = (isPieLike && !isDonut) || isFunnel;
	const pieCompact = parseFloat(slotHeight) <= 7 || showPieFunnelFooter;
	const fixedSlicePieLayout = useMemo(
		() => (fixedSliceDimension ? buildFixedSlicePieSeriesLayout({ multiMetricPie: multiRingPie, chartTheme }) : { padAngle: 2 }),
		[chartTheme, fixedSliceDimension, multiRingPie],
	);

	const chartData = useMemo(
		() =>
			buildCompositionChartRowsFromStats({
				primaryPhaseStats,
				orders,
				dimension: config.dimension,
				metrics: activeMetrics,
				selectedPhase,
				filters: statsContext?.statisticsFilters ?? {},
			}),
		[activeMetrics, config.dimension, orders, primaryPhaseStats, selectedPhase, statsContext],
	);

	const seriesRows = useMemo(() => {
		if (compareSeriesPie) {
			return compareSeriesList[0]?.items ?? [];
		}
		if (isCompositionChartFixedSliceDimension(config.dimension)) {
			return chartData.items;
		}
		return chartData.items.filter((row) => activeMetrics.some((metric) => getPrimaryPhaseRowMetricValue(row, metric) > 0));
	}, [activeMetrics, chartData.items, compareSeriesList, compareSeriesPie, config.dimension]);

	const [labelMode, setLabelMode] = useState(DONUT_PIE_LABEL_MODE.ALL);
	const [pickedLabelKeys, setPickedLabelKeys] = useState(() => new Set());
	const [visiblePieMetrics, setVisiblePieMetrics] = useState(() => new Set(activeMetrics));
	const [visibleCompareSeriesKeys, setVisibleCompareSeriesKeys] = useState(() => new Set(compareSeriesList.map((item) => item.key)));
	const [highlightKey, setHighlightKey] = useState(null);

	const summarySalesTotal = useMemo(() => {
		const sourceItems = compareSeriesPie ? seriesRows : chartData.items;
		const items = highlightKey ? sourceItems.filter((row) => row.key === highlightKey) : sourceItems;
		return items.reduce((sum, row) => sum + getPrimaryPhaseRowMetricValue(row, ORDER_PRIMARY_PHASE_METRICS.TOTAL_PRICE), 0);
	}, [chartData.items, compareSeriesPie, highlightKey, seriesRows]);

	const summaryOrderCount = useMemo(() => {
		const sourceItems = compareSeriesPie ? seriesRows : chartData.items;
		if (!highlightKey) {
			return compareSeriesPie
				? sourceItems.reduce((sum, row) => sum + getPrimaryPhaseRowMetricValue(row, ORDER_PRIMARY_PHASE_METRICS.COUNT), 0)
				: chartData.totalCount;
		}
		const row = sourceItems.find((item) => item.key === highlightKey);
		return row ? getPrimaryPhaseRowMetricValue(row, ORDER_PRIMARY_PHASE_METRICS.COUNT) : 0;
	}, [chartData.items, chartData.totalCount, compareSeriesPie, highlightKey, seriesRows]);

	const pieDataByMetric = useMemo(
		() =>
			activeMetrics.map((metric) =>
				seriesRows.map((row, index) => {
					const rawValue = getPrimaryPhaseRowMetricValue(row, metric);
					const preserveZeroSlot = multiRingPie && isCompositionChartFixedSliceDimension(config.dimension);
					return {
						name: row.label,
						value: rawValue > 0 ? rawValue : preserveZeroSlot ? Number.EPSILON : 0,
						rawValue,
						row,
						metric,
						phase: config.dimension === COMPOSITION_CHART_DIMENSION.PRIMARY_PHASE ? row.key : undefined,
						itemStyle: {
							color: resolveCompositionChartSliceColor(config.dimension, row, index, getThemeColor, isLight),
							...(rawValue <= 0 && preserveZeroSlot ? { opacity: 0 } : null),
						},
					};
				}),
			),
		[activeMetrics, config.dimension, getThemeColor, isLight, multiRingPie, seriesRows],
	);
	const pieDataByCompareSeries = useMemo(() => {
		if (!compareSeriesPie) return [];
		return compareSeriesList.map((series) =>
			(series.items ?? []).map((row, index) => {
				const rawValue = getPrimaryPhaseRowMetricValue(row, primaryMetric);
				const preserveZeroSlot = isCompositionChartFixedSliceDimension(config.dimension);
				return {
					name: row.label,
					value: rawValue > 0 ? rawValue : preserveZeroSlot ? Number.EPSILON : 0,
					rawValue,
					row,
					metric: primaryMetric,
					seriesKey: series.key,
					seriesLabel: series.label,
					phase: config.dimension === COMPOSITION_CHART_DIMENSION.PRIMARY_PHASE ? row.key : undefined,
					itemStyle: {
						color: resolveCompositionChartSliceColor(config.dimension, row, index, getThemeColor, isLight),
						...(rawValue <= 0 && preserveZeroSlot ? { opacity: 0 } : null),
					},
				};
			}),
		);
	}, [compareSeriesList, compareSeriesPie, config.dimension, getThemeColor, isLight, primaryMetric]);
	const pieData = useMemo(() => (compareSeriesPie ? pieDataByCompareSeries[0] ?? [] : pieDataByMetric[0] ?? []), [compareSeriesPie, pieDataByCompareSeries, pieDataByMetric]);
	const preserveSliceOrder =
		isCompositionChartFixedSliceDimension(config.dimension) || config.dimension === COMPOSITION_CHART_DIMENSION.PRIMARY_PHASE || seriesRows.some((row) => row.key === '__other__');

	useEffect(() => {
		setVisiblePieMetrics(new Set(activeMetrics));
	}, [activeMetrics]);

	useEffect(() => {
		setVisibleCompareSeriesKeys(new Set(compareSeriesList.map((item) => item.key)));
	}, [compareSeriesList]);

	useEffect(() => {
		setHighlightKey(null);
	}, [config.dimension, animationKey]);

	const visibleActiveMetrics = useMemo(() => activeMetrics.filter((metric) => visiblePieMetrics.has(metric)), [activeMetrics, visiblePieMetrics]);
	const visibleCompareSeries = useMemo(
		() => compareSeriesList.filter((series) => visibleCompareSeriesKeys.has(series.key)),
		[compareSeriesList, visibleCompareSeriesKeys],
	);

	const handleTogglePieMetricVisibility = useCallback((metric) => {
		setVisiblePieMetrics((prev) => {
			const next = new Set(prev);
			if (next.has(metric)) {
				if (next.size <= 1) return prev;
				next.delete(metric);
				return next;
			}
			next.add(metric);
			return next;
		});
	}, []);

	const handleLegendSelectChanged = useCallback(
		(params) => {
			const selected = params?.selected ?? {};
			if (compareSeriesPie) {
				setVisibleCompareSeriesKeys((prev) => {
					const next = new Set();
					for (const series of compareSeriesList) {
						if (selected[series.label] !== false) next.add(series.key);
					}
					return next.size > 0 ? next : prev;
				});
				return;
			}
			setVisiblePieMetrics((prev) => {
				const next = new Set();
				for (const metric of activeMetrics) {
					if (selected[getPrimaryPhaseMetricLabel(metric)] !== false) {
						next.add(metric);
					}
				}
				return next.size > 0 ? next : prev;
			});
		},
		[activeMetrics, compareSeriesList, compareSeriesPie],
	);

	const handleSliceClick = useCallback(
		(params) => {
			const row = params?.data?.row ?? seriesRows[params?.dataIndex];
			if (enableOrderDrill && onDrillSelect) {
				if (!row?.key) return;
				onDrillSelect({
					kind: 'custom',
					dimension: config.dimension,
					dimensionKey: row.key,
					otherKeys: row.otherKeys,
					label: row.label,
				});
				return;
			}
			if (row?.key) {
				setHighlightKey((prev) => (prev === row.key ? null : row.key));
			}
			const name = params?.data?.name;
			if (!name || labelMode !== DONUT_PIE_LABEL_MODE.PICK) return;
			setPickedLabelKeys((prev) => {
				const next = new Set(prev);
				if (next.has(name)) next.delete(name);
				else next.add(name);
				return next;
			});
		},
		[config.dimension, enableOrderDrill, labelMode, onDrillSelect, seriesRows],
	);

	const handleLabelModeChange = useCallback((mode) => {
		setLabelMode(mode);
		if (mode === DONUT_PIE_LABEL_MODE.ALL) setPickedLabelKeys(new Set());
	}, []);

	const handleSeriesDrillClick = useCallback(
		(params) => {
			const row = params?.data?.row ?? seriesRows[params?.dataIndex];
			if (enableOrderDrill && onDrillSelect) {
				if (!row?.key) return;
				onDrillSelect({
					kind: 'custom',
					dimension: config.dimension,
					dimensionKey: row.key,
					otherKeys: row.otherKeys,
					label: row.label,
				});
				return;
			}
			if (row?.key) {
				setHighlightKey((prev) => (prev === row.key ? null : row.key));
			}
		},
		[config.dimension, enableOrderDrill, onDrillSelect, seriesRows],
	);

	const donutChartEvents = useMemo(() => {
		const events = { click: handleSliceClick };
		if (multiRingPie) {
			events.legendselectchanged = handleLegendSelectChanged;
		}
		return events;
	}, [handleLegendSelectChanged, handleSliceClick, multiRingPie]);
	const seriesDrillEvents = useMemo(() => ({ click: handleSeriesDrillClick }), [handleSeriesDrillClick]);
	const pieHasLabelRoom = labelMode === DONUT_PIE_LABEL_MODE.ALL || pickedLabelKeys.size > 0;
	const selectedSliceKey = useMemo(() => {
		if (!highlightKey) return null;
		const row = seriesRows.find((item) => item.key === highlightKey);
		return row?.label ?? null;
	}, [highlightKey, seriesRows]);

	useEffect(() => {
		setPickedLabelKeys((prev) => {
			const valid = new Set(pieData.map((item) => item.name));
			const next = new Set([...prev].filter((key) => valid.has(key)));
			return next.size === prev.size ? prev : next;
		});
	}, [pieData]);

	const centerValue = compareSeriesPie
		? compareSeriesList[0]?.totalMetric ?? 0
		: currencyMetric
			? chartData.totalMetric
			: chartData.totalCount;
	const centerOrderCount = compareSeriesPie ? (compareSeriesList[0]?.totalCount ?? 0) : chartData.totalCount;
	const centerMetricTotals = useMemo(
		() =>
			sortActiveMetrics(activeMetrics).map((metric) => ({
				metric,
				value:
					metric === ORDER_PRIMARY_PHASE_METRICS.COUNT && !highlightKey
						? chartData.totalCount
						: seriesRows.reduce((sum, row) => sum + getPrimaryPhaseRowMetricValue(row, metric), 0),
			})),
		[activeMetrics, chartData.totalCount, highlightKey, seriesRows],
	);
	const multiMetricLabeledRingIndex = Math.max(
		(compareSeriesPie ? visibleCompareSeries.length : visibleActiveMetrics.length) - 1,
		0,
	);

	const collapsedMultiMetricPie =
		(multiMetricPie && visibleActiveMetrics.length === 1) || (compareSeriesPie && visibleCompareSeries.length === 1);

	const pieRadius = useMemo(
		() =>
			resolveLayeredMetricPieRadii({
				metricCount: compareSeriesPie
					? visibleCompareSeries.length
					: multiMetricPie
						? visibleActiveMetrics.length
						: activeMetrics.length,
				centerMetricCount: compareSeriesPie ? compareSeriesList.length : multiMetricPie ? activeMetrics.length : 1,
				isDonut,
				needsLabelRoom: pieHasLabelRoom,
				compact: pieCompact,
				largeCenter: isDonut && !multiRingPie && currencyMetric,
				expandedSingleRing: isDonut && collapsedMultiMetricPie,
			}),
		[
			activeMetrics.length,
			collapsedMultiMetricPie,
			compareSeriesList.length,
			compareSeriesPie,
			currencyMetric,
			isDonut,
			multiMetricPie,
			multiRingPie,
			pieCompact,
			pieHasLabelRoom,
			visibleActiveMetrics.length,
			visibleCompareSeries.length,
		],
	);

	const resolvePieCenterY = useCallback(
		(hasTopLegend, hasBottomLegend = false) => {
			if (multiRingPie) {
				if (hasTopLegend && hasBottomLegend) return '50%';
				if (hasTopLegend) return '52%';
				if (hasBottomLegend) return '48%';
				return '50%';
			}
			if (fixedSliceDimension && hasBottomLegend) return isDonut ? '46%' : '44%';
			if (!isDonut && pieData.length > 1 && labelMode === DONUT_PIE_LABEL_MODE.PICK && pickedLabelKeys.size === 0) return '44%';
			if (isDonut && pieData.length > 1 && labelMode === DONUT_PIE_LABEL_MODE.PICK && pickedLabelKeys.size === 0) return '44%';
			return '50%';
		},
		[fixedSliceDimension, isDonut, labelMode, multiRingPie, pieData.length, pickedLabelKeys.size],
	);

	const pieOption = useMemo(() => {
		const dimensionLegend =
			fixedSliceDimension && seriesRows.length > 1 ? buildFixedSliceLegendOption(seriesRows, config.dimension, getThemeColor, isLight, chartTheme, 'bottom') : null;
		const isCompositionSliceSelected = (item) => {
			if (selectedSliceKey != null) return item.name === selectedSliceKey;
			return labelMode === DONUT_PIE_LABEL_MODE.PICK && pickedLabelKeys.has(item.name);
		};

		if (compareSeriesPie) {
			const hasSeriesLegend = compareSeriesList.length > 1;
			const seriesLegendSelected = Object.fromEntries(
				compareSeriesList.map((series) => [series.label, visibleCompareSeriesKeys.has(series.key)]),
			);
			const legends = [
				hasSeriesLegend
					? enrichMetricLegendWithSummary(
							{
								show: true,
								top: 0,
								left: 'center',
								data: compareSeriesList.map((series) => series.label),
								selected: seriesLegendSelected,
								itemWidth: 8,
								itemHeight: 8,
								textStyle: { color: chartTheme.labelColor, fontSize: 10 },
							},
							chartTheme,
							summarySalesTotal,
							summaryOrderCount,
						)
					: null,
				dimensionLegend,
			].filter(Boolean);
			const hasSliceSelection = selectedSliceKey != null;
			const hasPicked = labelMode === DONUT_PIE_LABEL_MODE.PICK && pickedLabelKeys.size > 0;

			return {
				backgroundColor: 'transparent',
				tooltip: {
					...chartTheme.tooltip,
					trigger: 'item',
					formatter: (params) => {
						const row = params.data?.row;
						const metric = params.data?.metric ?? primaryMetric;
						const rawValue = params.data?.rawValue ?? params.value;
						if (rawValue <= 0) {
							return `${params.seriesName}<br/>${params.name}<br/>${getPrimaryPhaseMetricLabel(metric)} ${formatMetricValue(0, metric)} (0%)`;
						}
						const ringRows = params.data?.seriesKey
							? compareSeriesList.find((item) => item.key === params.data.seriesKey)?.items ?? seriesRows
							: seriesRows;
						const percentLabel = fixedSliceDimension ? formatCompositionChartSliceTruePercent(ringRows, row, metric) : params.percent;
						return `${params.seriesName}<br/>${params.name}<br/>${buildPrimaryPhaseMetricTooltipLine(row, metric)} (${percentLabel}%)`;
					},
				},
				legend: legends.length > 1 ? legends : (legends[0] ?? { show: false }),
				series: visibleCompareSeries.map((series, ringIndex) => {
					const sourceIndex = compareSeriesList.findIndex((item) => item.key === series.key);
					const ringData = pieDataByCompareSeries[sourceIndex] ?? [];
					const isLabeledRing = ringIndex === multiMetricLabeledRingIndex;
					const mappedData = isLabeledRing
						? mapDonutPieDataWithLabel(ringData, {
								chartTheme,
								getKey: (item) => item.name,
								labelMode,
								pickedKeys: pickedLabelKeys,
								selectedKey: selectedSliceKey,
								labelFormatter: (params) => {
									const row = params.data?.row;
									const rawValue = params.data?.rawValue ?? params.value;
									const percentLabel = fixedSliceDimension
										? formatCompositionChartSliceTruePercent(series.items, row, primaryMetric)
										: params.percent;
									return `${params.name}\n${formatMetricValue(rawValue, primaryMetric)} · ${percentLabel}%`;
								},
							})
						: ringData.map((item) => {
								const labelEmphasized = labelMode === DONUT_PIE_LABEL_MODE.PICK && pickedLabelKeys.has(item.name);
								const sliceSelected = isCompositionSliceSelected(item);
								const emphasized = hasSliceSelection ? sliceSelected : labelEmphasized;
								return {
									...item,
									itemStyle: {
										...item.itemStyle,
										opacity: (hasSliceSelection || hasPicked) && !emphasized ? 0.38 : 1,
										shadowBlur: hasSliceSelection && sliceSelected ? 10 : 0,
										shadowColor: hasSliceSelection && sliceSelected ? chartTheme.pieItemShadow : 'transparent',
									},
								};
							});
					return {
						name: series.label,
						type: 'pie',
						radius: pieRadius[ringIndex],
						center: ['50%', resolvePieCenterY(hasSeriesLegend, Boolean(dimensionLegend))],
						sort: preserveSliceOrder ? 'none' : 'desc',
						color: ringData.map((item) => item.itemStyle.color),
						selectedMode: false,
						avoidLabelOverlap: true,
						label: { show: false },
						labelLine: { show: false },
						cursor: 'pointer',
						data: mappedData,
						z: ringIndex + 1,
						animationDuration: 700,
						...fixedSlicePieLayout,
					};
				}),
			};
		}

		if (multiMetricPie) {
			const hasMetricLegend = activeMetrics.length > 1;
			const metricLegendSelected = Object.fromEntries(
				activeMetrics.map((metric) => [getPrimaryPhaseMetricLabel(metric), visiblePieMetrics.has(metric)]),
			);
			const legends = [
				hasMetricLegend
					? enrichMetricLegendWithSummary(
							{
								show: true,
								top: 0,
								left: 'center',
								data: activeMetrics.map((metric) => getPrimaryPhaseMetricLabel(metric)),
								selected: metricLegendSelected,
								itemWidth: 8,
								itemHeight: 8,
								textStyle: { color: chartTheme.labelColor, fontSize: 10 },
							},
							chartTheme,
							summarySalesTotal,
							summaryOrderCount,
						)
					: null,
				dimensionLegend,
			].filter(Boolean);
			const hasSliceSelection = selectedSliceKey != null;
			const hasPicked = labelMode === DONUT_PIE_LABEL_MODE.PICK && pickedLabelKeys.size > 0;

			return {
				backgroundColor: 'transparent',
				tooltip: {
					...chartTheme.tooltip,
					trigger: 'item',
					formatter: (params) => {
						const row = params.data?.row;
						const metric = params.data?.metric ?? primaryMetric;
						const rawValue = params.data?.rawValue ?? params.value;
						if (rawValue <= 0) {
							return `${params.seriesName}<br/>${params.name}<br/>${getPrimaryPhaseMetricLabel(metric)} ${formatMetricValue(0, metric)} (0%)`;
						}
						const percentLabel = fixedSliceDimension ? formatCompositionChartSliceTruePercent(seriesRows, row, metric) : params.percent;
						return `${params.seriesName}<br/>${params.name}<br/>${buildPrimaryPhaseMetricTooltipLine(row, metric)} (${percentLabel}%)`;
					},
				},
				legend: legends.length > 1 ? legends : (legends[0] ?? { show: false }),
				series: visibleActiveMetrics.map((metric, ringIndex) => {
					const sourceIndex = activeMetrics.indexOf(metric);
					const ringData = pieDataByMetric[sourceIndex] ?? [];
					const isLabeledRing = ringIndex === multiMetricLabeledRingIndex;
					const mappedData = isLabeledRing
						? mapDonutPieDataWithLabel(ringData, {
								chartTheme,
								getKey: (item) => item.name,
								labelMode,
								pickedKeys: pickedLabelKeys,
								selectedKey: selectedSliceKey,
								labelFormatter: (params) => {
									const row = params.data?.row;
									const rawValue = params.data?.rawValue ?? params.value;
									const percentLabel = fixedSliceDimension ? formatCompositionChartSliceTruePercent(seriesRows, row, metric) : params.percent;
									return `${params.name}\n${formatMetricValue(rawValue, metric)} · ${percentLabel}%`;
								},
							})
						: ringData.map((item) => {
								const labelEmphasized = labelMode === DONUT_PIE_LABEL_MODE.PICK && pickedLabelKeys.has(item.name);
								const sliceSelected = isCompositionSliceSelected(item);
								const emphasized = hasSliceSelection ? sliceSelected : labelEmphasized;
								return {
									...item,
									itemStyle: {
										...item.itemStyle,
										opacity: (hasSliceSelection || hasPicked) && !emphasized ? 0.38 : 1,
										shadowBlur: hasSliceSelection && sliceSelected ? 10 : 0,
										shadowColor: hasSliceSelection && sliceSelected ? chartTheme.pieItemShadow : 'transparent',
									},
								};
							});
					return {
						name: getPrimaryPhaseMetricLabel(metric),
						type: 'pie',
						radius: pieRadius[ringIndex],
						center: ['50%', resolvePieCenterY(hasMetricLegend, Boolean(dimensionLegend))],
						sort: preserveSliceOrder ? 'none' : 'desc',
						color: ringData.map((item) => item.itemStyle.color),
						selectedMode: false,
						avoidLabelOverlap: true,
						label: { show: false },
						labelLine: { show: false },
						cursor: 'pointer',
						data: mappedData,
						z: ringIndex + 1,
						animationDuration: 700,
						...fixedSlicePieLayout,
					};
				}),
			};
		}

		const singleRadius = pieRadius[0];
		const showPickLegend = !isDonut && pieData.length > 1 && labelMode === DONUT_PIE_LABEL_MODE.PICK && pickedLabelKeys.size === 0;
		const legends = [
			showPickLegend ? { show: true, bottom: 0, left: 'center', itemWidth: 8, itemHeight: 8, textStyle: { color: chartTheme.labelColor, fontSize: 10 } } : null,
			dimensionLegend,
		].filter(Boolean);
		const mappedPieData = mapDonutPieDataWithLabel(pieData, {
			chartTheme,
			getKey: (item) => item.name,
			labelMode,
			pickedKeys: pickedLabelKeys,
			selectedKey: selectedSliceKey,
			labelFormatter: (params) => {
				const row = params.data?.row;
				const rawValue = params.data?.rawValue ?? params.value;
				const percentLabel = fixedSliceDimension ? formatCompositionChartSliceTruePercent(seriesRows, row, primaryMetric) : params.percent;
				return `${params.name}\n${formatMetricValue(rawValue, primaryMetric)} · ${percentLabel}%`;
			},
		});

		return {
			backgroundColor: 'transparent',
			tooltip: {
				...chartTheme.tooltip,
				trigger: 'item',
				formatter: (params) => {
					const row = params.data?.row;
					const percentLabel = fixedSliceDimension ? formatCompositionChartSliceTruePercent(seriesRows, row, primaryMetric) : params.percent;
					return `${params.name}<br/>${buildPrimaryPhaseMetricTooltipLine(row, primaryMetric)} (${percentLabel}%)`;
				},
			},
			legend: legends.length > 1 ? legends : (legends[0] ?? { show: false }),
			series: [
				{
					type: 'pie',
					radius: singleRadius,
					center: ['50%', resolvePieCenterY(false, Boolean(dimensionLegend))],
					sort: preserveSliceOrder ? 'none' : 'desc',
					color: pieData.map((item) => item.itemStyle.color),
					selectedMode: false,
					avoidLabelOverlap: true,
					label: { show: false },
					labelLine: { show: false },
					cursor: 'pointer',
					data: mappedPieData,
					animationDuration: 700,
					...fixedSlicePieLayout,
				},
			],
		};
	}, [
		activeMetrics,
		chartTheme,
		compareSeriesList,
		compareSeriesPie,
		config.dimension,
		fixedSliceDimension,
		fixedSlicePieLayout,
		getThemeColor,
		isDonut,
		isLight,
		labelMode,
		multiMetricLabeledRingIndex,
		multiMetricPie,
		visibleActiveMetrics,
		visibleCompareSeries,
		visibleCompareSeriesKeys,
		visiblePieMetrics,
		pickedLabelKeys,
		pieData,
		pieDataByCompareSeries,
		pieDataByMetric,
		pieRadius,
		preserveSliceOrder,
		primaryMetric,
		resolvePieCenterY,
		selectedSliceKey,
		seriesRows,
		summaryOrderCount,
		summarySalesTotal,
	]);

	const funnelOption = useMemo(
		() => ({
			backgroundColor: 'transparent',
			tooltip: {
				...chartTheme.tooltip,
				trigger: 'item',
				formatter: (params) => {
					const row = params.data?.row;
					return `${params.name}<br/>${buildPrimaryPhaseMetricTooltipLine(row, primaryMetric)}`;
				},
			},
			legend: {
				show: pieData.length > 1,
				bottom: 0,
				left: 'center',
				itemWidth: 8,
				itemHeight: 8,
				textStyle: { color: chartTheme.labelColor, fontSize: 10 },
			},
			series: [
				{
					type: 'funnel',
					left: '8%',
					top: 8,
					bottom: pieData.length > 1 ? 28 : 8,
					width: '84%',
					minSize: '12%',
					maxSize: '100%',
					sort: preserveSliceOrder ? 'none' : 'descending',
					gap: 4,
					label: {
						show: true,
						position: 'inside',
						fontSize: 9,
						color: '#fff',
						formatter: (params) => `${params.name}\n${formatMetricValue(params.value, primaryMetric)}`,
					},
					itemStyle: {
						borderColor: chartTheme.pieBorderColor,
						borderWidth: 1,
					},
					emphasis: {
						label: { fontSize: 10 },
					},
					data: pieData.map((item) => ({
						name: item.name,
						value: item.value,
						row: item.row,
						itemStyle: item.itemStyle,
					})),
					animationDuration: 680,
				},
			],
		}),
		[chartTheme, pieData, preserveSliceOrder, primaryMetric],
	);

	const barOption = useMemo(() => {
		const categoryLabels = seriesRows.map((row) => row.label);
		const verticalCategoryLayout = buildPeriodCompareCategoryAxisLayout(categoryLabels, chartTheme);
		const horizontalCategoryZoom = buildCategoryAxisDataZoom({
			count: categoryLabels.length,
			chartTheme,
			categoryOnY: true,
		});
		const categoryAxis = {
			type: 'category',
			data: categoryLabels,
			...(horizontalBar ? { inverse: true } : {}),
			...chartTheme.axis,
			splitLine: { show: false },
			axisLabel: horizontalBar
				? { ...chartTheme.axis.axisLabel, fontSize: 9 }
				: { ...chartTheme.axis.axisLabel, ...verticalCategoryLayout.axisLabel },
		};
		const accentColor = getStatisticsAccentColor(isLight, getThemeColor);
		const categoryDataZoom = horizontalBar ? horizontalCategoryZoom : verticalCategoryLayout.dataZoom;
		const verticalGridBottom = Math.max(verticalCategoryLayout.gridBottom, categoryLabels.length > CATEGORY_AXIS_VISIBLE_COUNT ? 28 : 4);

		if (multiMetricCompare) {
			const bindings = resolveCompositionChartMetricBindings(activeMetrics);
			const { useDualAxis, leftName, rightName } = resolveCompositionChartYAxisLabels(bindings);
			const seriesDefs = bindings.map((binding, index) => ({
				...binding,
				name: binding.label,
				values: seriesRows.map((row) => getPrimaryPhaseRowMetricValue(row, binding.metric)),
				color: resolveMetricSeriesColor(index, accentColor, isLight),
			}));
			const seriesValuesByAxis = {
				0: seriesDefs.filter((item) => item.yAxisIndex === 0).map((item) => item.values),
				1: seriesDefs.filter((item) => item.yAxisIndex === 1).map((item) => item.values),
			};
			const valueAxes = useDualAxis
				? [
						{
							...buildCompareValueYAxis(chartTheme, { currencyMetric: false, values: seriesValuesByAxis[0] }),
							position: horizontalBar ? 'bottom' : 'left',
							name: leftName,
							nameTextStyle: { color: chartTheme.labelColor, fontSize: 9 },
						},
						{
							...buildCompareValueYAxis(chartTheme, { currencyMetric: true, values: seriesValuesByAxis[1] }),
							position: horizontalBar ? 'top' : 'right',
							name: rightName,
							nameTextStyle: { color: chartTheme.labelColor, fontSize: 9 },
						},
					]
				: [
						buildCompareValueYAxis(chartTheme, {
							currencyMetric: bindings[0]?.currencyMetric,
							values: seriesValuesByAxis[0].length ? seriesValuesByAxis[0] : seriesValuesByAxis[1],
						}),
					];

			return {
				backgroundColor: 'transparent',
				grid: {
					left: 8,
					right: useDualAxis ? 28 : 24,
					top: useDualAxis ? 18 : 10,
					bottom: horizontalBar ? resolveHorizontalCompareBarGridBottom(useDualAxis) : verticalGridBottom,
					containLabel: true,
				},
				legend: {
					show: seriesDefs.length > 1,
					top: 0,
					left: 'center',
					itemWidth: 8,
					itemHeight: 8,
					textStyle: { color: chartTheme.labelColor, fontSize: 10 },
				},
				tooltip: {
					...chartTheme.tooltip,
					trigger: 'axis',
					axisPointer: { type: 'shadow', shadowStyle: { color: chartTheme.axisPointerShadow } },
					formatter: (items) => {
						if (!items?.length) return '';
						const row = seriesRows[items[0].dataIndex];
						const lines = [items[0].name];
						for (const item of items) {
							const binding = bindings[item.seriesIndex];
							if (!binding) continue;
							lines.push(`${item.marker}${binding.label} ${formatMetricValue(item.value, binding.metric)}`);
						}
						if (row) lines.push(buildPrimaryPhaseMetricTooltipLine(row, primaryMetric));
						return lines.join('<br/>');
					},
				},
				xAxis: horizontalBar ? valueAxes : categoryAxis,
				yAxis: horizontalBar ? categoryAxis : valueAxes,
				...(categoryDataZoom.length ? { dataZoom: categoryDataZoom } : {}),
				series: seriesDefs.map((def, index) => ({
					name: def.name,
					type: 'bar',
					yAxisIndex: horizontalBar ? 0 : def.yAxisIndex,
					xAxisIndex: horizontalBar ? def.yAxisIndex : 0,
					data: def.values.map((value, dataIndex) => ({
						value,
						row: seriesRows[dataIndex],
						itemStyle: {
							color: def.color,
							borderRadius: resolveBarBorderRadius(horizontalBar),
						},
					})),
					barMaxWidth: 18,
					...(!horizontalBar ? resolveVerticalCompareBarGapSeriesProps(index) : null),
					emphasis: { focus: 'series' },
					label: {
						show: true,
						position: resolveBarLabelPosition(horizontalBar),
						fontSize: 9,
						color: chartTheme.labelColor,
						formatter: (params) => formatMetricValue(params.value, def.metric),
					},
					animationDuration: 680,
				})),
			};
		}

		const barData = seriesRows.map((row, index) => ({
			value: getPrimaryPhaseRowMetricValue(row, primaryMetric),
			row,
			itemStyle: {
				color: resolveCompositionChartSliceColor(config.dimension, row, index, getThemeColor, isLight),
				borderRadius: resolveBarBorderRadius(horizontalBar),
			},
		}));
		const valueAxis = buildCompareValueYAxis(chartTheme, {
			currencyMetric,
			values: barData.map((item) => item.value),
		});

		return {
			backgroundColor: 'transparent',
			grid: {
				left: 8,
				right: 24,
				top: 10,
				bottom: horizontalBar ? resolveHorizontalCompareBarGridBottom() : verticalGridBottom,
				containLabel: true,
			},
			tooltip: {
				...chartTheme.tooltip,
				trigger: 'axis',
				axisPointer: { type: 'shadow', shadowStyle: { color: chartTheme.axisPointerShadow } },
				formatter: (items) => {
					const item = items?.[0];
					if (!item) return '';
					const row = seriesRows[item.dataIndex];
					return `${item.name}<br/>${buildPrimaryPhaseMetricTooltipLine(row, primaryMetric)}`;
				},
			},
			xAxis: horizontalBar ? valueAxis : categoryAxis,
			yAxis: horizontalBar ? categoryAxis : valueAxis,
			...(categoryDataZoom.length ? { dataZoom: categoryDataZoom } : {}),
			series: [
				{
					type: 'bar',
					data: barData,
					barMaxWidth: 18,
					...(!horizontalBar ? resolveVerticalCompareBarGapSeriesProps(0) : null),
					emphasis: chartTheme.barEmphasis,
					label: {
						show: true,
						position: resolveBarLabelPosition(horizontalBar),
						fontSize: 10,
						color: chartTheme.labelColor,
						formatter: (params) => formatMetricValue(params.value, primaryMetric),
					},
					animationDuration: 680,
				},
			],
		};
	}, [activeMetrics, chartTheme, config.dimension, currencyMetric, getThemeColor, horizontalBar, isLight, multiMetricCompare, primaryMetric, seriesRows]);

	const lineOption = useMemo(() => {
		const categoryLabels = seriesRows.map((row) => row.label);
		const categoryLayout = buildPeriodCompareCategoryAxisLayout(categoryLabels, chartTheme);
		const accentColor = getStatisticsAccentColor(isLight, getThemeColor);

		if (multiMetricCompare) {
			const bindings = resolveCompositionChartMetricBindings(activeMetrics);
			const { useDualAxis, leftName, rightName } = resolveCompositionChartYAxisLabels(bindings);
			const seriesDefs = bindings.map((binding, index) => ({
				...binding,
				name: binding.label,
				values: seriesRows.map((row) => getPrimaryPhaseRowMetricValue(row, binding.metric)),
				color: resolveMetricSeriesColor(index, accentColor, isLight),
			}));
			const seriesValuesByAxis = {
				0: seriesDefs.filter((item) => item.yAxisIndex === 0).map((item) => item.values),
				1: seriesDefs.filter((item) => item.yAxisIndex === 1).map((item) => item.values),
			};
			const valueAxes = useDualAxis
				? [
						{
							...buildCompareValueYAxis(chartTheme, { currencyMetric: false, values: seriesValuesByAxis[0] }),
							position: 'left',
							name: leftName,
							nameTextStyle: { color: chartTheme.labelColor, fontSize: 9 },
						},
						{
							...buildCompareValueYAxis(chartTheme, { currencyMetric: true, values: seriesValuesByAxis[1] }),
							position: 'right',
							name: rightName,
							nameTextStyle: { color: chartTheme.labelColor, fontSize: 9 },
						},
					]
				: [
						buildCompareValueYAxis(chartTheme, {
							currencyMetric: bindings[0]?.currencyMetric,
							values: seriesValuesByAxis[0].length ? seriesValuesByAxis[0] : seriesValuesByAxis[1],
						}),
					];

			return {
				backgroundColor: 'transparent',
				grid: {
					left: 8,
					right: useDualAxis ? 28 : 16,
					top: seriesDefs.length > 1 ? 24 : 14,
					bottom: categoryLayout.gridBottom,
					containLabel: true,
				},
				legend: {
					show: seriesDefs.length > 1,
					top: 0,
					left: 'center',
					itemWidth: 8,
					itemHeight: 8,
					textStyle: { color: chartTheme.labelColor, fontSize: 10 },
				},
				tooltip: {
					...chartTheme.tooltip,
					trigger: 'axis',
					axisPointer: { type: 'line', lineStyle: { color: chartTheme.axisPointerShadow } },
					formatter: (items) => {
						if (!items?.length) return '';
						const row = seriesRows[items[0].dataIndex];
						const lines = [items[0].name];
						for (const item of items) {
							const binding = bindings[item.seriesIndex];
							if (!binding) continue;
							lines.push(`${item.marker}${binding.label} ${formatMetricValue(item.value, binding.metric)}`);
						}
						if (row) lines.push(buildPrimaryPhaseMetricTooltipLine(row, primaryMetric));
						return lines.join('<br/>');
					},
				},
				xAxis: {
					type: 'category',
					data: categoryLabels,
					boundaryGap: false,
					...chartTheme.axis,
					axisLabel: {
						...chartTheme.axis.axisLabel,
						...categoryLayout.axisLabel,
					},
				},
				yAxis: valueAxes,
				...(categoryLayout.dataZoom.length ? { dataZoom: categoryLayout.dataZoom } : {}),
				series: seriesDefs.map((def) => ({
					name: def.name,
					type: 'line',
					yAxisIndex: def.yAxisIndex,
					smooth: 0.35,
					data: def.values.map((value, dataIndex) => ({
						value,
						row: seriesRows[dataIndex],
					})),
					symbolSize: 6,
					lineStyle: { width: 2.5, color: def.color },
					itemStyle: { color: def.color },
					emphasis: { focus: 'series', scale: true },
					label: {
						show: true,
						position: 'top',
						fontSize: 9,
						color: chartTheme.labelColor,
						formatter: (params) => formatMetricValue(params.value, def.metric),
					},
					animationDuration: 680,
				})),
			};
		}

		const lineValues = seriesRows.map((row, index) => ({
			value: getPrimaryPhaseRowMetricValue(row, primaryMetric),
			row,
			itemStyle: { color: resolveCompositionChartSliceColor(config.dimension, row, index, getThemeColor, isLight) },
		}));

		return {
			backgroundColor: 'transparent',
			grid: { left: 8, right: 16, top: 14, bottom: categoryLayout.gridBottom, containLabel: true },
			tooltip: {
				...chartTheme.tooltip,
				trigger: 'axis',
				axisPointer: { type: 'line', lineStyle: { color: chartTheme.axisPointerShadow } },
				formatter: (items) => {
					const item = items?.[0];
					if (!item) return '';
					const row = seriesRows[item.dataIndex];
					return `${item.name}<br/>${buildPrimaryPhaseMetricTooltipLine(row, primaryMetric)}`;
				},
			},
			xAxis: {
				type: 'category',
				data: categoryLabels,
				boundaryGap: false,
				...chartTheme.axis,
				axisLabel: {
					...chartTheme.axis.axisLabel,
					...categoryLayout.axisLabel,
				},
			},
			yAxis: buildCompareValueYAxis(chartTheme, {
				currencyMetric,
				values: lineValues.map((item) => item.value),
			}),
			...(categoryLayout.dataZoom.length ? { dataZoom: categoryLayout.dataZoom } : {}),
			series: [
				{
					type: 'line',
					smooth: 0.35,
					data: lineValues,
					symbolSize: 6,
					lineStyle: { width: 2.5, color: accentColor },
					itemStyle: { color: accentColor },
					areaStyle: { color: accentColor, opacity: 0.08 },
					label: {
						show: true,
						position: 'top',
						fontSize: 9,
						color: chartTheme.labelColor,
						formatter: (params) => formatMetricValue(params.value, primaryMetric),
					},
					animationDuration: 680,
				},
			],
		};
	}, [activeMetrics, chartTheme, config.dimension, currencyMetric, getThemeColor, isLight, multiMetricCompare, primaryMetric, seriesRows]);

	const comboOption = useMemo(() => {
		if (!multiMetricCompare || !isMultiMetricMixed) return null;

		const categoryLabels = seriesRows.map((row) => row.label);
		const categoryLayout = buildPeriodCompareCategoryAxisLayout(categoryLabels, chartTheme);
		const accentColor = getStatisticsAccentColor(isLight, getThemeColor);
		const bindings = resolveCompositionChartMetricBindings(activeMetrics);
		const { useDualAxis, leftName, rightName } = resolveCompositionChartYAxisLabels(bindings);
		const seriesDefs = bindings.map((binding, index) => ({
			...binding,
			name: binding.label,
			values: seriesRows.map((row) => getPrimaryPhaseRowMetricValue(row, binding.metric)),
			color: resolveMetricSeriesColor(index, accentColor, isLight),
		}));
		const seriesValuesByAxis = {
			0: seriesDefs.filter((item) => item.yAxisIndex === 0).map((item) => item.values),
			1: seriesDefs.filter((item) => item.yAxisIndex === 1).map((item) => item.values),
		};
		const valueAxes = useDualAxis
			? [
					{
						...buildCompareValueYAxis(chartTheme, { currencyMetric: false, values: seriesValuesByAxis[0] }),
						position: 'left',
						name: leftName,
						nameTextStyle: { color: chartTheme.labelColor, fontSize: 9 },
					},
					{
						...buildCompareValueYAxis(chartTheme, { currencyMetric: true, values: seriesValuesByAxis[1] }),
						position: 'right',
						name: rightName,
						nameTextStyle: { color: chartTheme.labelColor, fontSize: 9 },
					},
				]
			: [
					buildCompareValueYAxis(chartTheme, {
						currencyMetric: bindings[0]?.currencyMetric,
						values: seriesValuesByAxis[0].length ? seriesValuesByAxis[0] : seriesValuesByAxis[1],
					}),
				];
		const hasAnyBar = activeMetrics.some((metric) => resolveMetricSeriesIsBar(metric, resolvedMetricSeriesTypes, config.chartType, isCompositionChartBarType));
		const useSplitCategoryXAxis = hasAnyBar;
		const categoryDataZoom = buildCategoryAxisDataZoom({
			count: categoryLabels.length,
			chartTheme,
			xAxisIndexList: useSplitCategoryXAxis
				? [MIXED_COMPARE_BAR_X_AXIS_INDEX, MIXED_COMPARE_LINE_X_AXIS_INDEX]
				: 0,
		});

		return {
			backgroundColor: 'transparent',
			grid: {
				left: 8,
				right: useDualAxis ? 28 : 16,
				top: seriesDefs.length > 1 ? 24 : 14,
				bottom: categoryLayout.gridBottom,
				containLabel: true,
			},
			legend: {
				show: seriesDefs.length > 1,
				top: 0,
				left: 'center',
				itemWidth: 8,
				itemHeight: 8,
				textStyle: { color: chartTheme.labelColor, fontSize: 10 },
			},
			tooltip: buildMixedOrAxisCompareTooltip({
				chartTheme,
				useItemTrigger: useSplitCategoryXAxis,
				axisPointer: {
					...buildCompareAxisPointer(chartTheme, config.chartType, resolvedMetricSeriesTypes),
					...(useSplitCategoryXAxis ? { link: [{ xAxisIndex: [MIXED_COMPARE_BAR_X_AXIS_INDEX, MIXED_COMPARE_LINE_X_AXIS_INDEX] }] } : {}),
				},
				axisFormatter: (items) => {
					if (!items?.length) return '';
					const row = seriesRows[items[0].dataIndex];
					const lines = [items[0].name];
					for (const item of items) {
						const binding = bindings[item.seriesIndex];
						if (!binding) continue;
						lines.push(`${item.marker}${binding.label} ${formatMetricValue(item.value, binding.metric)}`);
					}
					if (row) lines.push(buildPrimaryPhaseMetricTooltipLine(row, primaryMetric));
					return lines.join('<br/>');
				},
				seriesDefs,
				formatSeriesValue: (def, rawValue) => formatMetricValue(rawValue, def.metric),
			}),
			xAxis: useSplitCategoryXAxis
				? buildMixedCompareCategoryXAxes(categoryLabels, chartTheme, {
						barAxisExtra: {
							axisLabel: {
								...chartTheme.axis.axisLabel,
								...categoryLayout.axisLabel,
							},
						},
					})
				: {
						type: 'category',
						data: categoryLabels,
						boundaryGap: hasAnyBar,
						...chartTheme.axis,
						axisLabel: {
							...chartTheme.axis.axisLabel,
							...categoryLayout.axisLabel,
						},
					},
			yAxis: valueAxes,
			...(categoryDataZoom.length ? { dataZoom: categoryDataZoom } : {}),
			series: seriesDefs.map((def, index) => {
				const seriesIsBar = resolveMetricSeriesIsBar(def.metric, resolvedMetricSeriesTypes, config.chartType, isCompositionChartBarType);
				const lineXAxisIndex = useSplitCategoryXAxis ? MIXED_COMPARE_LINE_X_AXIS_INDEX : 0;
				const seriesItem = buildMetricSeriesItem({
					name: def.name,
					values: def.values.map((value, dataIndex) => ({
						value,
						row: seriesRows[dataIndex],
					})),
					yAxisIndex: def.yAxisIndex,
					xAxisIndex: seriesIsBar ? MIXED_COMPARE_BAR_X_AXIS_INDEX : lineXAxisIndex,
					chartMetric: def.metric,
					color: def.color,
					lineSmooth: 0.35,
					showPointLabels: true,
					chartTheme,
					barBorderRadius: resolveBarBorderRadius(false),
					barLabelPosition: resolveBarLabelPosition(false),
					isBar: seriesIsBar,
					barMaxWidth: 18,
				});
				if (seriesIsBar) {
					Object.assign(seriesItem, resolveVerticalCompareBarGapSeriesProps(index), resolveMixedCompareBarSeriesExtra());
				} else if (useSplitCategoryXAxis) {
					Object.assign(seriesItem, { z: MIXED_COMPARE_LINE_Z, zlevel: 1, triggerLineEvent: true });
				} else {
					seriesItem.symbolSize = 6;
					seriesItem.triggerLineEvent = true;
				}
				return seriesItem;
			}),
		};
	}, [activeMetrics, chartTheme, config.chartType, getThemeColor, isLight, isMultiMetricMixed, multiMetricCompare, primaryMetric, resolvedMetricSeriesTypes, seriesRows]);

	const chartSummary = useMemo(
		() => ({ salesTotal: summarySalesTotal, orderCount: summaryOrderCount }),
		[summaryOrderCount, summarySalesTotal],
	);
	const barDisplayOption = useMemo(
		() => (barOption ? applyChartSummaryLegend(barOption, chartSummary, chartTheme) : null),
		[barOption, chartSummary, chartTheme],
	);
	const lineDisplayOption = useMemo(
		() => (lineOption ? applyChartSummaryLegend(lineOption, chartSummary, chartTheme) : null),
		[chartSummary, chartTheme, lineOption],
	);
	const comboDisplayOption = useMemo(
		() => (comboOption ? applyChartSummaryLegend(comboOption, chartSummary, chartTheme) : null),
		[chartSummary, chartTheme, comboOption],
	);

	if (isLoading) {
		if (isPreview) {
			return (
				<div className={['statistics-composition-chart', previewClass].filter(Boolean).join(' ')} style={frameStyle}>
					<StatisticsCardLoading className='statistics-lite-chart__loading statistics-lite-chart__loading--fill' fill />
				</div>
			);
		}

		return <StatisticsCardLoading className={['statistics-lite-chart__loading', previewClass].filter(Boolean).join(' ')} style={frameStyle} />;
	}

	if (isMap) {
		return (
			<CompositionChartMap
				orders={orders}
				geoAggregates={primaryPhaseStats?.chartAggregates?.geo}
				dimension={config.dimension}
				metric={config.metric}
				selectedPhase={selectedPhase}
				isLoading={isLoading}
				animationKey={animationKey}
				height={height}
				previewHeight={previewHeight}
				fillSlot={fillPreview}
				enableOrderDrill={enableOrderDrill}
				onDrillSelect={onDrillSelect}
			/>
		);
	}

	if (!seriesRows.length && !compareSeriesPie) {
		return (
			<StatisticsChartEmpty className={previewClass} style={isPreview ? frameStyle : undefined}>
				{STATISTICS_EMPTY_CHART}
			</StatisticsChartEmpty>
		);
	}

	if (isPieLike) {
		const hasDimensionLegend = fixedSliceDimension && seriesRows.length > 1;
		const hasLegend =
			hasDimensionLegend ||
			multiRingPie ||
			(!isDonut && pieData.length > 1) ||
			(isDonut && pieData.length > 1 && labelMode === DONUT_PIE_LABEL_MODE.PICK && pickedLabelKeys.size === 0);
		const pieFrameStyle = showPieFunnelFooter && isPreview ? { height: '100%', width: '100%', minHeight: 0 } : frameStyle;
		const multiRingCount = compareSeriesPie ? compareSeriesList.length : activeMetrics.length;
		const pieCenterY = resolvePieCenterY(compareSeriesPie ? compareSeriesList.length > 1 : multiMetricPie, hasDimensionLegend);
		return wrapPreviewPaint(
			<div
				className={[
					'statistics-lite-chart__pie-wrap',
					pieHasLabelRoom ? 'statistics-lite-chart__pie-wrap--selected' : null,
					'statistics-composition-chart',
					isDonut ? 'statistics-composition-chart--donut' : 'statistics-composition-chart--pie',
					showPieFunnelFooter ? 'statistics-composition-chart--with-summary-footer' : null,
					multiRingPie ? 'statistics-composition-chart--multi-metric-pie' : null,
					multiRingPie && multiRingCount >= 3 ? 'statistics-composition-chart--triple-metric-pie' : null,
					hasLegend ? 'statistics-composition-chart--has-legend' : null,
					previewClass,
				]
					.filter(Boolean)
					.join(' ')}
				style={isPreview ? frameStyle : undefined}
			>
				{!enableOrderDrill ? <StatisticsDonutLabelModeSwitch mode={labelMode} onChange={handleLabelModeChange} /> : null}
				<StatisticsChartFrame className={pieHasLabelRoom ? 'statistics-chart-frame--donut-selected' : ''} style={pieFrameStyle}>
					<ReactEchart
						key={`custom-pie-${config.chartType}-${config.dimension}-${activeMetrics.join(',')}-${animationKey}`}
						echarts={echarts}
						option={pieOption}
						onEvents={donutChartEvents}
						onChartReady={onPreviewChartReady}
						style={echartStyle}
					/>
				</StatisticsChartFrame>
				{isDonut ? (
					<div
						className={['statistics-lite-chart__pie-center', multiMetricPie ? 'statistics-lite-chart__pie-center--dual-metric' : null]
							.filter(Boolean)
							.join(' ')}
						style={{ top: pieCenterY }}
					>
						{multiMetricPie ? (
							<div className='statistics-lite-chart__pie-center-link statistics-lite-chart__pie-center-dual'>
								{centerMetricTotals.map(({ metric, value }) => {
									const isVisible = visiblePieMetrics.has(metric);
									const metricLabel = getPrimaryPhaseMetricLabel(metric);
									return (
										<button
											key={metric}
											type='button'
											className={['statistics-lite-chart__pie-center-metric-row', !isVisible ? 'is-inactive' : null].filter(Boolean).join(' ')}
											onClick={() => handleTogglePieMetricVisibility(metric)}
											aria-pressed={isVisible}
											title={isVisible ? `隐藏${metricLabel}` : `显示${metricLabel}`}
										>
											<span className='statistics-lite-chart__pie-center-metric-hint'>{metricLabel}</span>
											{isPrimaryPhaseCurrencyMetric(metric) ? (
												<StatisticsCount value={value} loading={isLoading} mode='currency' duration={0.45} />
											) : (
												<span className='statistics-lite-chart__pie-center-count-row'>
													<StatisticsCount value={value} loading={isLoading} duration={0.45} />
													<span className='statistics-lite-chart__pie-center-count-suffix'>单</span>
												</span>
											)}
										</button>
									);
								})}
							</div>
						) : (
							<strong
								className={[
									'statistics-lite-chart__pie-center-link',
									currencyMetric ? 'statistics-lite-chart__pie-center-currency' : 'statistics-composition-chart__center',
								]
									.filter(Boolean)
									.join(' ')}
							>
								{currencyMetric ? (
									<>
										<StatisticsCount value={centerValue} loading={isLoading} mode='currency' duration={0.45} />
										<span className='statistics-composition-chart__center-order-count'>
											<StatisticsCount value={centerOrderCount} loading={isLoading} duration={0.45} />
											<span className='statistics-composition-chart__center-label'>单</span>
										</span>
									</>
								) : (
									<span className='statistics-composition-chart__center-count-row'>
										<StatisticsCount value={centerValue} loading={isLoading} duration={0.45} />
										<span className='statistics-composition-chart__center-label'>单</span>
									</span>
								)}
							</strong>
						)}
					</div>
				) : null}
				{showPieFunnelFooter ? (
					<StatisticsChartSummaryStats salesTotal={summarySalesTotal} orderCount={summaryOrderCount} loading={isLoading} placement='footer' />
				) : null}
			</div>,
		);
	}

	if (isFunnel) {
		return wrapPreviewPaint(
			<div
				className={['statistics-composition-chart', 'statistics-composition-chart--funnel', 'statistics-composition-chart--with-summary-footer', previewClass]
					.filter(Boolean)
					.join(' ')}
				style={isPreview ? frameStyle : undefined}
			>
				<StatisticsChartFrame style={frameStyle}>
					<ReactEchart
						key={`custom-funnel-${config.dimension}-${activeMetrics.join(',')}-${animationKey}`}
						echarts={echarts}
						option={funnelOption}
						onEvents={seriesDrillEvents}
						onChartReady={onPreviewChartReady}
						style={echartStyle}
					/>
				</StatisticsChartFrame>
				<StatisticsChartSummaryStats salesTotal={summarySalesTotal} orderCount={summaryOrderCount} loading={isLoading} placement='footer' />
			</div>,
		);
	}

	if (isMultiMetricMixed && comboDisplayOption) {
		return wrapPreviewPaint(
			<div
				className={['statistics-composition-chart', 'statistics-composition-chart--combo', previewClass].filter(Boolean).join(' ')}
				style={isPreview ? frameStyle : undefined}
			>
				<StatisticsChartFrame style={frameStyle}>
					<ReactEchart
						key={`custom-combo-${config.dimension}-${activeMetrics.join(',')}-${JSON.stringify(resolvedMetricSeriesTypes)}-${animationKey}`}
						echarts={echarts}
						option={comboDisplayOption}
						onEvents={seriesDrillEvents}
						onChartReady={onPreviewChartReady}
						style={echartStyle}
					/>
				</StatisticsChartFrame>
			</div>,
		);
	}

	if (isLine) {
		return wrapPreviewPaint(
			<div
				className={['statistics-composition-chart', 'statistics-composition-chart--line', previewClass].filter(Boolean).join(' ')}
				style={isPreview ? frameStyle : undefined}
			>
				<StatisticsChartFrame style={frameStyle}>
					<ReactEchart
						key={`custom-line-${config.dimension}-${activeMetrics.join(',')}-${animationKey}`}
						echarts={echarts}
						option={lineDisplayOption}
						onEvents={seriesDrillEvents}
						onChartReady={onPreviewChartReady}
						style={echartStyle}
					/>
				</StatisticsChartFrame>
			</div>,
		);
	}

	return wrapPreviewPaint(
		<div
			className={['statistics-composition-chart', 'statistics-composition-chart--bar', previewClass].filter(Boolean).join(' ')}
			style={isPreview ? frameStyle : undefined}
		>
			<StatisticsChartFrame style={frameStyle}>
				<ReactEchart
					key={`custom-bar-${config.dimension}-${activeMetrics.join(',')}-${config.barOrientation}-${animationKey}`}
					echarts={echarts}
					option={barDisplayOption}
					onEvents={seriesDrillEvents}
					onChartReady={onPreviewChartReady}
					style={echartStyle}
				/>
			</StatisticsChartFrame>
		</div>,
	);
};

CompositionChart.propTypes = {
	orders: PropTypes.array,
	primaryPhaseStats: PropTypes.object,
	chartConfig: PropTypes.object,
	compareSeriesMatrix: PropTypes.shape({
		metric: PropTypes.string,
		series: PropTypes.arrayOf(PropTypes.object),
	}),
	selectedPhase: PropTypes.string,
	isLoading: PropTypes.bool,
	animationKey: PropTypes.string,
	height: PropTypes.string,
	previewHeight: PropTypes.string,
	fillPreview: PropTypes.bool,
	enableOrderDrill: PropTypes.bool,
	onDrillSelect: PropTypes.func,
	darkShell: PropTypes.bool,
};

export default memo(CompositionChart);
